import { ArrowLeft, Copy } from "lucide-react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { formatDateTime } from "../../lib/baseline/format";
import { categoryTone, severityTone } from "../../lib/events";
import { copyText } from "../../lib/utils";
import { useSession } from "../../services/SessionContext";

export function EventDetailPage() {
  const { uploadId, eventId } = useParams();
  const { repo } = useSession();
  if (!uploadId || !eventId) return <Navigate to="/app/dashboard" replace />;
  const upload = repo.getUpload(uploadId);
  const event = repo.getUploadEvents(uploadId).find((item) => item.eventId === eventId);
  if (!upload || !event) return <Navigate to={`/app/uploads/${uploadId}`} replace />;

  return (
    <div className="mx-auto max-w-3xl space-y-5 pb-20 md:pb-0">
      <Button asChild variant="ghost">
        <Link to={`/app/uploads/${uploadId}`}>
          <ArrowLeft className="h-4 w-4" aria-hidden /> Back to timeline
        </Link>
      </Button>
      <Card>
        <CardHeader>
          <div className="flex flex-wrap gap-2">
            <Badge className={categoryTone(event.category)}>{event.category.replace("_", " ")}</Badge>
            <Badge className={severityTone(event.severity)}>{event.severity}</Badge>
          </div>
          <CardTitle className="text-2xl">{event.title}</CardTitle>
          <p className="text-sm text-slate-600">{formatDateTime(event.timestamp)}</p>
        </CardHeader>
        <CardContent className="space-y-5">
          <section>
            <h2 className="text-sm font-bold uppercase text-slate-500">What this means</h2>
            <p className="mt-2 leading-7 text-slate-800">{event.plainEnglish}</p>
          </section>
          <section className="grid gap-3 sm:grid-cols-3">
            <Info label="Subject" value={`${event.subjectLabel} (${event.subjectCode})`} />
            <Info label="Action" value={`${event.actionLabel} (${event.actionCode})`} />
            <Info label="Trigger" value={`${event.triggerLabel} (${event.triggerCode})`} />
          </section>
          <section>
            <h2 className="text-sm font-bold uppercase text-slate-500">Parsed details</h2>
            {event.detailEntries.length ? (
              <dl className="mt-3 grid gap-3 sm:grid-cols-2">
                {event.detailEntries.map((detail, index) => (
                  <div key={`${detail.code}-${index}`} className="rounded-md border border-slate-200 p-3">
                    <dt className="text-xs font-semibold uppercase text-slate-500">
                      {detail.label} ({detail.code})
                    </dt>
                    <dd className="mt-1 font-medium text-navy-900">{detail.rawValue || "blank"}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="mt-2 text-sm text-slate-600">No detail values were included in this row.</p>
            )}
          </section>
          <section>
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-sm font-bold uppercase text-slate-500">Raw event</h2>
              <Button size="sm" variant="secondary" onClick={() => copyText(event.rawRow)}>
                <Copy className="h-4 w-4" aria-hidden /> Copy
              </Button>
            </div>
            <pre className="mt-3 overflow-x-auto rounded-md bg-slate-950 p-4 text-xs text-white">{event.rawRow}</pre>
          </section>
        </CardContent>
      </Card>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-slate-200 p-3">
      <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
      <p className="mt-1 font-medium text-navy-900">{value}</p>
    </div>
  );
}
